import { JSONValue } from "../components/JsonEditor";
import { blankState, Recipe, State } from "../components/state";
import { objectsAreEqual } from "../utils";
import { checkSharedStateAuth } from "./checkSharedStateAuth";
import { createEntry } from "./createEntry";
import { refreshToken } from "./getAuth";
import { readEntry, ReadEntryResponse } from "./readEntry";
import { updateEntry } from "./updateEntry";

// TODO - better error handling

// export const apiEndpoint = "http://localhost:4000/v1";
// process.env.SHOPPING_LIST_API_ENDPOINT ??
export const apiEndpoint =
  "https://ee5j7ok3xl.execute-api.us-east-1.amazonaws.com/prod/v1"; // "http://localhost:4000/v1";
export const allRecipesKey = "/shopping-list/recipes/all-recipes";

export interface RetryApiProps {
  readonly func: Function; // TODO - could probably just use caller
  readonly funcProps: any;
  readonly response: Response;
  readonly token: string | null;
}
export async function retryApi(props: RetryApiProps) {
  const { func, funcProps, response, token } = props;
  if ([401, 403].includes(response.status)) {
    if (token === localStorage.getItem("authToken")) {
      const myHeaders = new Headers({
        "Oauth-Token": token ?? "",
        Authorization: token ?? "",
      });
      const refreshed = await refreshToken();
      if (refreshed.token) {
        const authToken = refreshed.token;
        const refreshToken = refreshed.refreshToken;
        localStorage.setItem("authToken", authToken);
        localStorage.setItem("refreshToken", refreshToken);
      }
    }
    return await func({
      ...funcProps,
      skipRetry: true,
    });
  }
  throw new Error(`Failed to get parameter: ${response.statusText}`);
}
export interface SaveStateProps {
  readonly state: State;
  readonly email: string;
  readonly forceSave?: boolean;
  readonly lastState?: State;
}

export function sanitizeKey(input: string): string {
  return input.replace(/[^a-zA-Z0-9_.-]/g, "-");
}

export interface GetStateParameterPathProps {
  readonly email: string;
  readonly sharedStateName?: string;
}

export function getStateParameterPath(props: GetStateParameterPathProps) {
  const { email, sharedStateName } = props;
  const sanitizedEmail =
    sharedStateName && sharedStateName !== ""
      ? sanitizeKey(sharedStateName)
      : sanitizeKey(email);
  const parameterPath = `/shopping-list/state/${sanitizedEmail}/state`;
  return parameterPath;
}

export interface GetRecipeParameterPathProps {
  readonly name: string;
  readonly author?: string;
}
export function getRecipeParameterPath(props: GetRecipeParameterPathProps) {
  const { name, author } = props;
  const sanitizedName = sanitizeKey(name);
  const sanitizedAuthor = sanitizeKey(author ?? "all");
  const parameterPath = `/shopping-list/recipes/${sanitizedAuthor}/${sanitizedName}`;
  return parameterPath;
}

export async function saveState(props: SaveStateProps) {
  const { state, email, forceSave, lastState } = props;
  if (!forceSave && objectsAreEqual(state, blankState)) return;
  if (
    !forceSave &&
    Object.keys(state.menu).length === 0 &&
    state.additionalItems.length === 0
  ) {
    return;
  }
  const parameterPath = getStateParameterPath({
    email,
    sharedStateName: state.settings?.sharedStateName,
  });
  try {
    await updateEntry({
      name: parameterPath,
      value: JSON.stringify(state),
      lastValue: forceSave ? undefined : JSON.stringify(lastState),
      type: "String",
    });
  } catch (err: any) {
    console.error({ err });
    if (err.message === "The conditional request failed") {
      throw err;
    }
  }
}

export interface LoadStateProps {
  readonly email: string;
  readonly sharedStateName?: string;
}
export async function loadState(props: LoadStateProps) {
  const { email, sharedStateName } = props;
  const parameterPath = getStateParameterPath({
    email,
    sharedStateName: sharedStateName,
  });
  try {
    const resp = await readEntry({ name: parameterPath });
    return resp?.value;
  } catch (err: any) {
    console.error({ err });
  }
}
export async function writeRecipe(props: Recipe) {
  const parameterPath = getRecipeParameterPath({ ...props });
  await createEntry({
    name: parameterPath,
    value: JSON.stringify(props),
    type: "String",
  });
  await updateAllRecipes({ ...props });
}
export async function updateRecipe(props: Recipe) {
  const parameterPath = getRecipeParameterPath({ ...props });
  await updateEntry({
    name: parameterPath,
    value: JSON.stringify(props),
    type: "String",
  });
  await updateAllRecipes({ ...props });
}
async function updateAllRecipes(props: Recipe) {
  let response: ReadEntryResponse | undefined;
  try {
    response = await readEntry({ name: allRecipesKey });
  } catch (err: any) {
    // TODO type error messages & contract between front/backends
    if (err.message === "Failed to get parameter: Not Found") {
      response = {
        value: "",
        name: allRecipesKey,
      };
    }
  }
  if (response && response!.value) {
    const allRecipes =
      Object.keys(response.value).length > 0
        ? (JSON.parse(response.value) as Record<string, Recipe>)
        : {};
    allRecipes[props.name] = props;
    if (response.value.length > 0) {
      updateEntry({
        name: allRecipesKey,
        value: JSON.stringify(allRecipes),
      });
    } else {
      createEntry({
        name: allRecipesKey,
        value: JSON.stringify(allRecipes),
      });
    }
  } else {
    createEntry({
      name: allRecipesKey,
      value: JSON.stringify({ [props.name]: props }),
    });
  }
}
export async function loadRecipes() {
  try {
    const response = await readEntry({ name: allRecipesKey });
    if (response) {
      return JSON.parse(response.value) as Record<string, Recipe>;
    }
  } catch (err: any) {
    console.error({ err });
  }
  return {};
}

export interface IsAuthorizedSharedStateProps {
  readonly sharedStateName: JSONValue;
}
export async function isAuthorizedSharedState(
  props: IsAuthorizedSharedStateProps,
) {
  const { sharedStateName } = props;
  if (sharedStateName && sharedStateName !== "") {
    const response = await checkSharedStateAuth({
      name: sharedStateName,
    });
    if (response) {
      return response.value;
    }
  }
  return false;
}
